'use client';

import { useMemo } from 'react';
import { ParentFilterType } from 'src/context/reducers/filterSetReducer';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';

export interface FilterSet {
  id: string;
  label: string;
  type: ParentFilterType;
  scope: string;
  is_company: boolean;
}

interface UseFilterSetUtilsOptions {
  isCompany?: boolean;
  filterType?: ParentFilterType | string;
  searchQuery?: string;
}

/**
 * Custom hook for filtering and grouping filter sets
 *
 * @param options Configuration options
 * @param options.isCompany Filter by company status (true for company filters, false for personal filters)
 * @param options.filterType Filter by specific filter type
 * @param options.searchQuery Search query for filter sets
 *
 * @returns Object containing filtered and grouped filter sets, along with loading and error states
 */
export const useFilterSetUtils = ({
  isCompany,
  filterType,
  searchQuery = '',
}: UseFilterSetUtilsOptions = {}) => {
  const {
    data: filterSets,
    error,
    isLoading,
    refetch,
  } = useGetFilterSetsQuery({ q: searchQuery, serializer: 'detailed' });

  // Filter filter sets based on company status and/or type
  const filteredFilterSets = useMemo(() => {
    if (!filterSets?.results) return [];

    return filterSets.results
      .filter((filterSet) => {
        // Filter by company status if specified
        if (isCompany && filterSet.scope !== 'Company') {
          return false;
        }

        // Filter by type if specified
        if (filterType !== undefined && filterSet.type !== filterType) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filterSets, isCompany, filterType]);

  // Group filter sets by type
  const groupedFilterSets = useMemo(() => {
    if (!filteredFilterSets.length) return {};

    return filteredFilterSets.reduce((acc, filterSet) => {
      const type = filterSet.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(filterSet);
      return acc;
    }, {} as Record<ParentFilterType, MedScout.FilterSet[]>);
  }, [filteredFilterSets]);

  // Group filter sets by scope
  const groupedByScope = useMemo(() => {
    if (!filteredFilterSets || !filteredFilterSets.length) return {};

    return filteredFilterSets.reduce((acc, filterSet) => {
      const scope = filterSet.scope;
      if (!acc[scope]) {
        acc[scope] = [];
      }

      acc[scope].push(filterSet);
      return acc;
    }, {} as Record<string, MedScout.FilterSet[]>);
  }, [filteredFilterSets]);

  return {
    filterSets: filteredFilterSets,
    groupedFilterSets,
    groupedByScope,
    error,
    isLoading,
    refetch,
  };
};

/**
 * Custom hook to get all filter sets of type FILTER
 *
 * @param searchQuery Optional search query for filter sets
 * @returns Object containing all filter sets, along with loading and error states
 */
export const useAllFilterSetTypes = (searchQuery = '') => {
  // Get all filter sets without filtering by type
  const {
    data: allFilterSets,
    error,
    isLoading,
    refetch,
  } = useGetFilterSetsQuery({
    q: searchQuery,
    serializer: 'detailed',
  });

  // Filter and group filter sets by type
  const filterSetsByType = useMemo(() => {
    if (!allFilterSets?.results)
      return {
        FILTER: [],
      };

    const sorted = allFilterSets.results.sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    // Initialize with empty array for FILTER type
    const result = {
      FILTER: [],
    };

    // Group by type
    sorted.forEach((filterSet) => {
      if (filterSet.type === 'FILTER') {
        result[filterSet.type].push(filterSet);
      }
    });

    return result;
  }, [allFilterSets]);

  // Get all filter sets as a single array
  const allFilterSetsCombined = useMemo(() => {
    return [...filterSetsByType.FILTER];
  }, [filterSetsByType]);

  // Group all filter sets by scope
  const allFilterSetsByScope = useMemo(() => {
    if (!allFilterSetsCombined.length) return {};

    return allFilterSetsCombined.reduce((acc, filterSet) => {
      const scope = filterSet.scope;
      if (!acc[scope]) {
        acc[scope] = [];
      }

      acc[scope].push(filterSet);
      return acc;
    }, {} as Record<string, MedScout.FilterSet[]>);
  }, [allFilterSetsCombined]);
  return {
    allFilterSets: allFilterSetsCombined,
    filterSetsByType,
    allFilterSetsByScope,
    error,
    isLoading,
    refetch,
  };
};

export default useFilterSetUtils;
